import React from 'react';
import classnames from 'classnames/bind';
import { useFormContext } from 'react-hook-form';

import Node, { getPropertyValue, hasProperty } from 'types/node';
import ReteInput from 'pipelines/services/rete/controls/input';
import { DeployTimeEditableNode } from 'types/pipeline';

import { useReteNode } from 'pipelines/hooks/useReteNode';
import {
  GroupedControls,
  useGroupedNodeControls,
} from 'deployments/hooks/useGroupedNodeControls';
import {
  CurrentNodeProvider,
  useCurrentNode,
} from 'deployments/hooks/useCurrentNode';

import {
  NodeCategory,
  getCategoryByNodeName,
} from 'pipelines/services/rete/nodes/categories';

import { Heading } from 'components/Heading/Heading';
import { Icon } from 'components/Icon/IconDeprecated';
import { Text } from 'components/Text/Text';
import { Button } from 'components/Button/Button';

import { useDeploymentForm } from '../context/deployment_form_context';
import { DeploymentFormControl } from '../Control/Control';
import styles from './Node.module.scss';

const c = classnames.bind(styles);

export type DeploymentFormNodeProps = {
  node: Node;
  deployTimeEditableNodes: DeployTimeEditableNode[];
};

export function DeploymentFormNode({
  node,
  deployTimeEditableNodes,
}: DeploymentFormNodeProps) {
  const reteNode = useReteNode(node);
  const { readonly } = useDeploymentForm();
  const controls = React.useMemo(() => {
    if (!reteNode) {
      return undefined;
    }

    return Array.from(reteNode.controls.values());
  }, [reteNode]);
  const groupedControls = useGroupedNodeControls(controls);

  if (!node || !node.name || !reteNode || (!readonly && !controls?.length)) {
    return null;
  }

  const { component } = node;
  if (!component) {
    return null;
  }
  const category = getCategoryByNodeName(node.name);

  return (
    <CurrentNodeProvider nodeID={node.id!} node={node} reteNode={reteNode}>
      <Inner
        groupedControls={groupedControls}
        category={category}
        deployTimeEditableNodes={deployTimeEditableNodes}
      />
    </CurrentNodeProvider>
  );
}

type InnerProps = {
  groupedControls: GroupedControls;
  category?: NodeCategory;
} & Pick<DeploymentFormNodeProps, 'deployTimeEditableNodes'>;

function Inner({
  groupedControls,
  category,
  deployTimeEditableNodes,
}: InnerProps) {
  const { node, reteNode } = useCurrentNode();
  const { pipeline, deployment, readonly } = useDeploymentForm();
  const { watch, setValue } = useFormContext();
  const { unfilledProperties } = node;
  const controls = Object.keys(groupedControls).flatMap(
    (group) => groupedControls[group] as ReteInput[]
  );
  const description =
    pipeline && node.id ? pipeline.nodeDescriptions[node.id] : undefined;

  const isUnfilled = (key: string) => unfilledProperties.includes(key);
  function keyRefDependents(key: string) {
    return controls.filter(({ keyref }) => keyref === key);
  }

  function isDeployParam(control: ReteInput): boolean {
    const deployNode = deployTimeEditableNodes.find(
      ({ nodeID }) => nodeID === node.id
    );

    const userSpecifiedControl = deployNode?.controls.find(
      ({ key }) => key === control.key
    );
    const isUserChecked = userSpecifiedControl
      ? userSpecifiedControl.deploymentParam &&
        userSpecifiedControl.deploymentParam !== 'never'
      : undefined;

    const isDefaultChecked =
      control.deploymentParam && control.deploymentParam !== 'never';

    return (isUserChecked ?? isDefaultChecked) as unknown as boolean;
  }

  function defaultValue(control: ReteInput) {
    const pipelineDefinitionValue = getPropertyValue(node.data, control.key);
    const hasDeploymentConfigValue = hasProperty(
      deployment?.configuration[node.id!],
      control.key
    );
    const deploymentConfigValue = getPropertyValue(
      deployment?.configuration[node.id!],
      control.key
    );

    const controlName = `${node.id}.${control.key}`;
    const fallbackValue = hasDeploymentConfigValue
      ? deploymentConfigValue
      : pipelineDefinitionValue;

    return watch(controlName, fallbackValue);
  }

  function isActiveKeyRef(control: ReteInput) {
    return keyRefDependents(control.key)
      .filter(
        ({ requiresKeyrefValue }) =>
          requiresKeyrefValue === defaultValue(control)
      )
      .some(
        (control) =>
          (control.props.required && isUnfilled(control.key)) ||
          isDeployParam(control)
      );
  }

  function isUnusedHiddenKeyRef(control: ReteInput) {
    return (
      !readonly &&
      control.props.type === 'hidden' &&
      keyRefDependents(control.key).every(
        (control) =>
          !isDeployParam(control) &&
          (!control.props.required || !isUnfilled(control.key))
      )
    );
  }

  function hasNoDeployParams() {
    // Determine potential keyRef node controls
    const keyRefs = controls.filter(
      ({ requiresKeyrefValue }) => requiresKeyrefValue === undefined
    );

    // Check if a node has any keyRef controls on deployment dialog
    const hasNoKeyRefs = keyRefs.every(
      (control) =>
        !readonly &&
        (!control.props.required || !isUnfilled(control.key)) &&
        (!isDeployParam(control) || control.props.type === 'hidden')
    );

    // Check if node has keyRefDependents that actively use a keyRef value on deployment dialog
    const hasNoKeyRefDependents = controls
      .filter(
        ({ requiresKeyrefValue, keyref }) =>
          requiresKeyrefValue !== undefined &&
          keyref &&
          keyRefs.some(
            (control) =>
              keyref === control.key &&
              defaultValue(control) === requiresKeyrefValue
          )
      )
      .every(
        (control) =>
          !readonly &&
          (!control.props.required || !isUnfilled(control.key)) &&
          !isDeployParam(control)
      );

    return hasNoKeyRefs && hasNoKeyRefDependents;
  }

  const deployTimeControls = controls.filter((control) => isDeployParam(control));
  const hasDeployTimeControls = deployTimeControls.length > 0;
  const [resetSignal, setResetSignal] = React.useState(0);

  function resetDeployTimeProperties() {
    deployTimeControls.forEach((control) => {
      const pipelineDefinitionValue = getPropertyValue(node.data, control.key);
      const value =
        pipelineDefinitionValue === undefined
          ? null
          : structuredClone(pipelineDefinitionValue);

      setValue(`${node.id}.${control.key}`, value, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });

      if (!control.keyref) {
        return;
      }

      const pipelineDefinitionKeyrefValue = getPropertyValue(
        node.data,
        control.keyref
      );
      const keyrefValue =
        pipelineDefinitionKeyrefValue === undefined
          ? null
          : structuredClone(pipelineDefinitionKeyrefValue);

      setValue(`${node.id}.${control.keyref}`, keyrefValue, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
    });

    setResetSignal((previous) => previous + 1);
  }

  if (hasNoDeployParams()) {
    return null;
  }

  return (
    <fieldset key={node.id} className={c('wrap')} name={node.name}>
      <div className={c('node')}>
        <span className={c('node-icon', 'theme', category?.theme)}>
          <Icon name={node.component!.icon} />
        </span>

        <div className={c('node-header')}>
          <div className={c('node-heading')}>
            <Heading level="3" asChild>
              <legend>
                {node.name} <em>{node.id}</em>
              </legend>
            </Heading>

            {!readonly && hasDeployTimeControls && (
              <Button
                variant="ghost"
                size="xsmall"
                className={c('node-reset')}
                onClick={resetDeployTimeProperties}
              >
                Reset to defaults
              </Button>
            )}
          </div>

          {description && (
            <Text className={c('node-description')} type="paragraph">
              {description}
            </Text>
          )}
        </div>

        <div className={c('node-properties')}>
          {Object.keys(groupedControls).map((group) => {
            const controls = groupedControls[group] as ReteInput[];
            const groupID = `${node.id}_${group}`;

            if (controls.length <= 0) {
              return null;
            }

            return (
              <fieldset
                className={c('group')}
                aria-labelledby={groupID}
                key={groupID}
              >
                {group && (
                  <strong
                    id={groupID}
                    className={c('group-label', 'label-headline')}
                  >
                    {group}
                  </strong>
                )}

                {controls.map((control) => (
                  <DeploymentFormControl
                    property={control.key}
                    control={control}
                    node={node}
                    reteNode={reteNode}
                    defaultValue={defaultValue(control)}
                    isUnusedHiddenKeyRef={isUnusedHiddenKeyRef(control)}
                    isActiveKeyRef={isActiveKeyRef(control)}
                    isDeployParam={isDeployParam(control)}
                    isUnfilled={isUnfilled(control.key)}
                    resetSignal={resetSignal}
                    key={`${node.id}-${control.key}`}
                  />
                ))}
              </fieldset>
            );
          })}
        </div>
      </div>
    </fieldset>
  );
}

const Memoized = React.memo(Inner);

export { Memoized as Inner };
